import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	AuditAction,
	LoanDefinitionType,
	PersonRelationType,
	SecurityType,
} from '@dealstack/shared/lib/generated';

export const formatAuditActionToDisplay = (action: AuditAction, tCapManage): string => {
	switch (action) {
		case AuditAction.CREATE:
			return tCapManage('transactions.details.history.action_create');
		case AuditAction.UPDATE:
			return tCapManage('transactions.details.history.action_update');
		case AuditAction.DELETE:
			return tCapManage('transactions.details.history.action_delete');
		case AuditAction.RESTORE:
			return tCapManage('transactions.details.history.action_restore');
		default:
			return action;
	}
};

export const formatAuditSessionTitle = (
	action: string | undefined,
	tCapManage: (key: string) => string
): string | undefined => {
	switch (action) {
		case AuditAction.CREATE:
			return tCapManage('audit.session.createdTitle');
		case AuditAction.UPDATE:
			return tCapManage('audit.session.updatedTitle');
		case AuditAction.DELETE:
			return tCapManage('audit.session.deletedTitle');
		case AuditAction.RESTORE:
			return tCapManage('audit.session.restoredTitle');
		default:
			return undefined;
	}
};

const TRANSACTION_ALLOWED_FIELDS = [
	'units',
	'cost',
	'date',
	'type',
	'securityId',
	'destinationId',
	'sourceId',
	'fromMipId',
	'toMipId',
] as const;
const SECURITY_ALLOWED_FIELDS = [
	'name',
	'type',
	'currency',
	'issuerId',
	'issuedIn',
	'nominalValue',
	'votesPerUnit',
	'fairMarketValuePerUnit',
	'equityBasis',
	'residueRights',
	'unitDenominator',
	'authorised_capital',
] as const;

const PERSON_ALLOWED_FIELDS = [
	'firstName',
	'lastName',
	'displayName',
	'email',
	'phoneNumber',
	'telephonePrefix',
	'dateOfBirth',
	'gender',
	'placeOfBirth',
	'countryOfBirth',
	'citizenship',
	'domicile',
	'type',
	'providerDetailsFriendlyName',
	'providerDetailsLegalForm',
] as const;

const BANK_ACCOUNT_ALLOWED_FIELDS = [
	'countryCode',
	'currency',
	'investorInstructions',
	'status',
] as const;

// Columns MIP-2541 dropped — the values live on BankAccountIdentifier now. Audits written
// before the cutover still carry them, and are the only record of what the details were
// then, so they stay renderable. Nothing new writes them.
const BANK_ACCOUNT_LEGACY_FIELDS = [
	'bankName',
	'accountHolder',
	'iban',
	'swiftCode',
	'accountNumber',
	'country',
] as const;

const TAX_RESIDENCE_ALLOWED_FIELDS = ['countryCode', 'authority', 'number'] as const;

const BANK_ACCOUNT_IDENTIFIER_ALLOWED_FIELDS = ['key', 'value', 'bankAccountFormatRuleId'] as const;

const ADDRESS_ALLOWED_FIELDS = [
	'country',
	'city',
	'postCode',
	'street',
	'houseNumber',
	'singleAddressString',
] as const;

const PERSON_SCOPE_ALLOWED_FIELDS = ['isManagementEntity', 'canIssueSecurities'] as const;

const PERSON_RELATION_ALLOWED_FIELDS = [
	'relationType',
	'sourcePersonId',
	'targetPersonId',
] as const;

const INVESTMENT_WORKFLOW_ALLOWED_FIELDS = [
	'type',
	'status',
	'paymentStatus',
	'isCompleted',
	'completionStep',
	'paymentAnnotation',
	'targetStartDate',
	'targetCompletionDate',
	'startedAt',
	'approvedAt',
] as const;

const SHARE_ASSOCIATION_ALLOWED_FIELDS = [
	'securityName',
	'number',
	'cost',
	'transactionType',
	'expectedTransactionType',
	'vestingStartDate',
	'isNegative',
] as const;

const LOAN_ALLOWED_FIELDS = ['loanDefinitionName', 'amount', 'amountRemaining'] as const;

const EMPLOYMENT_RECORD_ALLOWED_FIELDS = [
	'jobTitle',
	'employeeReference',
	'startDate',
	'endDate',
	'isPrimaryForEmployee',
] as const;

export const ALLOWED_FIELDS_BY_TABLE: Readonly<Record<string, readonly string[]>> = {
	Transaction: TRANSACTION_ALLOWED_FIELDS,
	Security: SECURITY_ALLOWED_FIELDS,
	Person: PERSON_ALLOWED_FIELDS,
	BankAccount: [...BANK_ACCOUNT_ALLOWED_FIELDS, ...BANK_ACCOUNT_LEGACY_FIELDS],
	BankAccountIdentifier: BANK_ACCOUNT_IDENTIFIER_ALLOWED_FIELDS,
	TaxResidence: TAX_RESIDENCE_ALLOWED_FIELDS,
	Address: ADDRESS_ALLOWED_FIELDS,
	PersonScope: PERSON_SCOPE_ALLOWED_FIELDS,
	PersonPerson: PERSON_RELATION_ALLOWED_FIELDS,
	InvestmentWorkflow: INVESTMENT_WORKFLOW_ALLOWED_FIELDS,
	ShareAssociation: SHARE_ASSOCIATION_ALLOWED_FIELDS,
	Loan: LOAN_ALLOWED_FIELDS,
	EmploymentRecord: EMPLOYMENT_RECORD_ALLOWED_FIELDS,
};

type TransactionField = (typeof TRANSACTION_ALLOWED_FIELDS)[number];
type SecurityField = (typeof SECURITY_ALLOWED_FIELDS)[number];
type PersonField = (typeof PERSON_ALLOWED_FIELDS)[number];
type BankAccountField =
	| (typeof BANK_ACCOUNT_ALLOWED_FIELDS)[number]
	| (typeof BANK_ACCOUNT_LEGACY_FIELDS)[number];
type BankAccountIdentifierField = (typeof BANK_ACCOUNT_IDENTIFIER_ALLOWED_FIELDS)[number];
type TaxResidenceField = (typeof TAX_RESIDENCE_ALLOWED_FIELDS)[number];
type AddressField = (typeof ADDRESS_ALLOWED_FIELDS)[number];
type PersonScopeField = (typeof PERSON_SCOPE_ALLOWED_FIELDS)[number];
type PersonRelationField = (typeof PERSON_RELATION_ALLOWED_FIELDS)[number];
type InvestmentWorkflowField = (typeof INVESTMENT_WORKFLOW_ALLOWED_FIELDS)[number];
type ShareAssociationField = (typeof SHARE_ASSOCIATION_ALLOWED_FIELDS)[number];
type LoanField = (typeof LOAN_ALLOWED_FIELDS)[number];
type EmploymentRecordField = (typeof EMPLOYMENT_RECORD_ALLOWED_FIELDS)[number];

export const formatTableNameToLabel = (
	tableName: string | undefined,
	tCapManage: (key: string, options?: { defaultValue: string }) => string
): string => {
	switch (tableName) {
		case 'Person':
			return tCapManage('audit.record_type.person', { defaultValue: 'Person' });
		case 'BankAccount':
			return tCapManage('audit.record_type.bank_account', { defaultValue: 'Bank account' });
		case 'BankAccountIdentifier':
			return tCapManage('audit.record_type.bank_account_identifier', {
				defaultValue: 'Bank account detail',
			});
		case 'TaxResidence':
			return tCapManage('audit.record_type.tax_residence', {
				defaultValue: 'Tax residence',
			});
		case 'Address':
			return tCapManage('audit.record_type.address', { defaultValue: 'Address' });
		case 'PersonScope':
			return tCapManage('audit.record_type.person_scope', {
				defaultValue: 'Deal membership',
			});
		case 'PersonPerson':
			return tCapManage('audit.record_type.person_person', {
				defaultValue: 'Related person',
			});
		case 'Security':
			return tCapManage('audit.record_type.security', { defaultValue: 'Security' });
		case 'Transaction':
			return tCapManage('audit.record_type.transaction', { defaultValue: 'Transaction' });
		case 'InvestmentWorkflow':
			return tCapManage('audit.record_type.investment_workflow', {
				defaultValue: 'Investment',
			});
		case 'ShareAssociation':
			return tCapManage('audit.record_type.share_association', {
				defaultValue: 'Allocation',
			});
		case 'Loan':
			return tCapManage('audit.record_type.loan', { defaultValue: 'Loan' });
		case 'EmploymentRecord':
			return tCapManage('audit.record_type.employment_record');
		case 'HoldingEvent':
			return tCapManage('audit.record_type.holding_event');
		default:
			return tableName ?? '';
	}
};

export type AuditFieldValue = string | number | boolean | null | undefined | Date;

type SecurityOption = {
	value: string;
	label: string;
	unitDenominator?: number;
};

export const shouldShowField = (key: string, tableName: string): boolean => {
	const list = ALLOWED_FIELDS_BY_TABLE?.[tableName];
	return list?.includes(key);
};

export const formatFieldName = (key: string, tCapManage, tableName?: string): string => {
	const fieldMappings: Record<TransactionField, string> = {
		units: tCapManage('transactions.drawer.number_of_units.label'),
		cost: tCapManage('transactions.drawer.price.label'),
		date: tCapManage('transactions.drawer.date.label'),
		type: tCapManage('transactions.drawer.transaction_type.label'),
		securityId: tCapManage('transactions.drawer.security_name.label'),
		destinationId: tCapManage('transactions.drawer.buyer.label'),
		sourceId: tCapManage('transactions.drawer.seller.label'),
		fromMipId: tCapManage('transactions.drawer.seller_holding.label'),
		toMipId: tCapManage('transactions.drawer.buyer_mip.label'),
	};

	if (tableName === 'Security') {
		const securityFieldMappings: Record<SecurityField, string> = {
			name: tCapManage('securities.fields.name', { defaultValue: 'Name' }),
			type: tCapManage('securities.fields.type', { defaultValue: 'Type' }),
			currency: tCapManage('securities.fields.currency', { defaultValue: 'Currency' }),
			issuerId: tCapManage('securities.fields.issuer', { defaultValue: 'Issuer' }),
			issuedIn: tCapManage('securities.fields.issued_in', { defaultValue: 'Issued In' }),
			nominalValue: tCapManage('securities.fields.nominal_value', {
				defaultValue: 'Nominal Value',
			}),
			votesPerUnit: tCapManage('securities.fields.votes_per_unit', {
				defaultValue: 'Votes Per Unit',
			}),
			fairMarketValuePerUnit: tCapManage('securities.fields.fair_market_value_per_unit', {
				defaultValue: 'FMV per Unit',
			}),
			equityBasis: tCapManage('securities.fields.equity_basis', {
				defaultValue: 'Equity Basis',
			}),
			residueRights: tCapManage('securities.fields.residue_rights', {
				defaultValue: 'Residue Rights',
			}),
			unitDenominator: tCapManage('securities.fields.unit_denominator', {
				defaultValue: 'Unit Denominator',
			}),
			authorised_capital: tCapManage('securities.fields.authorised_capital', {
				defaultValue: 'Authorised Capital',
			}),
		};

		return securityFieldMappings[key] || key;
	}

	if (tableName === 'Person') {
		const personFieldMappings: Record<PersonField, string> = {
			firstName: tCapManage('people.fields.first_name', { defaultValue: 'First Name' }),
			lastName: tCapManage('people.fields.last_name', { defaultValue: 'Last Name' }),
			displayName: tCapManage('people.fields.display_name', { defaultValue: 'Display Name' }),
			email: tCapManage('people.fields.email', { defaultValue: 'Email' }),
			phoneNumber: tCapManage('people.fields.phone_number', { defaultValue: 'Phone Number' }),
			telephonePrefix: tCapManage('people.fields.telephone_prefix', {
				defaultValue: 'Telephone Prefix',
			}),
			dateOfBirth: tCapManage('people.fields.date_of_birth', {
				defaultValue: 'Date of Birth',
			}),
			gender: tCapManage('people.fields.gender', { defaultValue: 'Gender' }),
			placeOfBirth: tCapManage('people.fields.place_of_birth', {
				defaultValue: 'Place of Birth',
			}),
			countryOfBirth: tCapManage('people.fields.country_of_birth', {
				defaultValue: 'Country of Birth',
			}),
			citizenship: tCapManage('people.fields.citizenship', { defaultValue: 'Citizenship' }),
			domicile: tCapManage('people.fields.domicile', { defaultValue: 'Domicile' }),
			type: tCapManage('people.fields.type', { defaultValue: 'Type' }),
			providerDetailsFriendlyName: tCapManage('people.fields.legal_name', {
				defaultValue: 'Legal Name',
			}),
			providerDetailsLegalForm: tCapManage('people.fields.legal_form', {
				defaultValue: 'Legal Form',
			}),
		};

		return personFieldMappings[key] || key;
	}

	if (tableName === 'HoldingEvent') {
		const pledgeTo = tCapManage('holdings.event.pledge_to');
		const pledgeAgainst = tCapManage('holdings.event.pledge_against');
		const assignedTo = tCapManage('holdings.event.assigned_to');
		const document = tCapManage('holdings.event.document');
		const holdingEventFieldMappings: Record<string, string> = {
			type: tCapManage('deal_event_log.holding.event_type'),
			securityName: tCapManage('transactions.drawer.security_name.label'),
			holderName: tCapManage('holdings.table.columns.holder'),
			date: tCapManage('transactions.drawer.date.label'),
			note: tCapManage('holdings.event.note_to'),
			pledgeToPersonId: pledgeTo,
			pledgeToString: pledgeTo,
			pledgeFromHolderId: pledgeAgainst,
			pledgeFromSecurityId: pledgeAgainst,
			pledgeFromString: pledgeAgainst,
			assignedToPersonId: assignedTo,
			assignedToString: assignedTo,
			documentUrl: document,
			documentName: document,
			fileId: document,
		};

		return holdingEventFieldMappings[key] || key;
	}

	if (tableName === 'BankAccount') {
		const bankAccountFieldMappings: Record<BankAccountField, string> = {
			bankName: tCapManage('people.fields.bank_account.bank_name', {
				defaultValue: 'Bank Name',
			}),
			accountHolder: tCapManage('people.fields.bank_account.account_holder', {
				defaultValue: 'Account Holder',
			}),
			iban: tCapManage('people.fields.bank_account.iban', { defaultValue: 'IBAN' }),
			swiftCode: tCapManage('people.fields.bank_account.swift_code', {
				defaultValue: 'SWIFT / BIC',
			}),
			accountNumber: tCapManage('people.fields.bank_account.account_number', {
				defaultValue: 'Account Number',
			}),
			country: tCapManage('people.fields.bank_account.country', { defaultValue: 'Country' }),
			countryCode: tCapManage('people.fields.bank_account.country_code', {
				defaultValue: 'Country Code',
			}),
			currency: tCapManage('people.fields.bank_account.currency', {
				defaultValue: 'Currency',
			}),
			investorInstructions: tCapManage('people.fields.bank_account.instructions', {
				defaultValue: 'Instructions',
			}),
			status: tCapManage('people.fields.bank_account.status', { defaultValue: 'Status' }),
		};

		return bankAccountFieldMappings[key] || key;
	}

	if (tableName === 'BankAccountIdentifier') {
		const identifierFieldMappings: Record<BankAccountIdentifierField, string> = {
			key: tCapManage('people.fields.bank_account.identifier_key', {
				defaultValue: 'Field',
			}),
			value: tCapManage('people.fields.bank_account.identifier_value', {
				defaultValue: 'Value',
			}),
			bankAccountFormatRuleId: tCapManage('people.fields.bank_account.entry_format', {
				defaultValue: 'Entry format',
			}),
		};

		return identifierFieldMappings[key] || key;
	}

	if (tableName === 'EmploymentRecord') {
		const employmentFieldMappings: Record<EmploymentRecordField, string> = {
			jobTitle: tCapManage('people.fields.employment.job_title'),
			employeeReference: tCapManage('people.fields.employment.employee_reference'),
			startDate: tCapManage('people.fields.employment.start_date'),
			endDate: tCapManage('people.fields.employment.end_date'),
			isPrimaryForEmployee: tCapManage('people.fields.employment.is_primary'),
		};

		return employmentFieldMappings[key] || key;
	}

	if (tableName === 'TaxResidence') {
		const taxResidenceFieldMappings: Record<TaxResidenceField, string> = {
			countryCode: tCapManage('people.fields.tax_residence.country', {
				defaultValue: 'Country',
			}),
			authority: tCapManage('people.fields.tax_residence.authority', {
				defaultValue: 'Tax Authority',
			}),
			number: tCapManage('people.fields.tax_residence.number', {
				defaultValue: 'Tax ID Number',
			}),
		};

		return taxResidenceFieldMappings[key] || key;
	}

	if (tableName === 'Address') {
		const addressFieldMappings: Record<AddressField, string> = {
			country: tCapManage('people.fields.address.country', { defaultValue: 'Country' }),
			city: tCapManage('people.fields.address.city', { defaultValue: 'City' }),
			postCode: tCapManage('people.fields.address.post_code', { defaultValue: 'Post Code' }),
			street: tCapManage('people.fields.address.street', { defaultValue: 'Street' }),
			houseNumber: tCapManage('people.fields.address.house_number', {
				defaultValue: 'House Number',
			}),
			singleAddressString: tCapManage('people.fields.address.full_address', {
				defaultValue: 'Address',
			}),
		};

		return addressFieldMappings[key] || key;
	}

	if (tableName === 'PersonScope') {
		const personScopeFieldMappings: Record<PersonScopeField, string> = {
			isManagementEntity: tCapManage('people.fields.scope.is_management_entity', {
				defaultValue: 'Management Entity',
			}),
			canIssueSecurities: tCapManage('people.fields.scope.can_issue_securities', {
				defaultValue: 'Can Issue Securities',
			}),
		};

		return personScopeFieldMappings[key] || key;
	}

	if (tableName === 'PersonPerson') {
		const personRelationFieldMappings: Record<PersonRelationField, string> = {
			relationType: tCapManage('people.fields.relation.relation_type', {
				defaultValue: 'Relationship',
			}),
			sourcePersonId: tCapManage('people.fields.relation.source_person', {
				defaultValue: 'From',
			}),
			targetPersonId: tCapManage('people.fields.relation.target_person', {
				defaultValue: 'To',
			}),
		};

		return personRelationFieldMappings[key] || key;
	}

	if (tableName === 'InvestmentWorkflow') {
		const investmentWorkflowFieldMappings: Record<InvestmentWorkflowField, string> = {
			type: tCapManage('investment_details.history.fields.type', { defaultValue: 'Type' }),
			status: tCapManage('investment_details.history.fields.status', {
				defaultValue: 'Status',
			}),
			paymentStatus: tCapManage('investment_details.history.fields.payment_status', {
				defaultValue: 'Payment status',
			}),
			isCompleted: tCapManage('investment_details.history.fields.is_completed', {
				defaultValue: 'Completed',
			}),
			completionStep: tCapManage('investment_details.history.fields.completion_step', {
				defaultValue: 'Completion step',
			}),
			paymentAnnotation: tCapManage('investment_details.history.fields.payment_annotation', {
				defaultValue: 'Payment note',
			}),
			targetStartDate: tCapManage('investment_details.history.fields.target_start_date', {
				defaultValue: 'Target start date',
			}),
			targetCompletionDate: tCapManage(
				'investment_details.history.fields.target_completion_date',
				{ defaultValue: 'Target completion date' }
			),
			startedAt: tCapManage('investment_details.history.fields.started_at', {
				defaultValue: 'Started at',
			}),
			approvedAt: tCapManage('investment_details.history.fields.approved_at', {
				defaultValue: 'Approved at',
			}),
		};

		return investmentWorkflowFieldMappings[key] || key;
	}

	if (tableName === 'ShareAssociation') {
		const shareAssociationFieldMappings: Record<ShareAssociationField, string> = {
			securityName: tCapManage('investment_details.history.fields.security', {
				defaultValue: 'Security',
			}),
			number: tCapManage('investment_details.history.fields.units', {
				defaultValue: 'Units',
			}),
			cost: tCapManage('investment_details.history.fields.cost', { defaultValue: 'Cost' }),
			transactionType: tCapManage('investment_details.history.fields.transaction_type', {
				defaultValue: 'Transaction type',
			}),
			expectedTransactionType: tCapManage(
				'investment_details.history.fields.expected_transaction_type',
				{ defaultValue: 'Expected transaction type' }
			),
			vestingStartDate: tCapManage('investment_details.history.fields.vesting_start_date', {
				defaultValue: 'Vesting start date',
			}),
			isNegative: tCapManage('investment_details.history.fields.is_negative', {
				defaultValue: 'Is reduction',
			}),
		};

		return shareAssociationFieldMappings[key] || key;
	}

	if (tableName === 'Loan') {
		const loanFieldMappings: Record<LoanField, string> = {
			loanDefinitionName: tCapManage('investment_details.history.fields.loan_definition', {
				defaultValue: 'Loan type',
			}),
			amount: tCapManage('investment_details.history.fields.amount', {
				defaultValue: 'Amount',
			}),
			amountRemaining: tCapManage('investment_details.history.fields.amount_remaining', {
				defaultValue: 'Amount remaining',
			}),
		};

		return loanFieldMappings[key] || key;
	}

	return fieldMappings[key] || key;
};

const getEntityName = (
	entityId: string,
	personsSelectOptions?: Array<{ value: string; label: string }>
): string => {
	if (!entityId) {
		return LONG_DASH;
	}
	const entity = personsSelectOptions?.find((option) => option.value === entityId);
	return entity ? entity.label : LONG_DASH;
};

const getMipName = (
	entityId: string,
	mipSelectOptions?: Array<{ value: string; label: string }>
): string => {
	if (!entityId) {
		return LONG_DASH;
	}
	const entity = mipSelectOptions?.find((option) => option.value === entityId);
	return entity ? entity.label : LONG_DASH;
};

const getSecurityName = (
	securityId: string,
	securitiesSelectOptions?: Array<SecurityOption>
): string => {
	if (!securityId) {
		return LONG_DASH;
	}
	const security = securitiesSelectOptions?.find((option) => option.value === securityId);
	return security ? security.label : LONG_DASH;
};

const extractDecimalPrecisionFromUnitDenominator = (unitDenominator?: number | null): number => {
	return Math.log10(unitDenominator || 1);
};

const PERSON_RELATION_TYPE_LABELS: Partial<Record<PersonRelationType, string>> = {
	[PersonRelationType.PIV]: 'PIV',
	[PersonRelationType.REPRESENTATIVE]: 'Representative',
};

const formatPersonRelationType = (value: string): string => {
	const labels: Partial<Record<string, string>> = PERSON_RELATION_TYPE_LABELS;
	return labels[value] ?? value;
};

export const formatValue = (
	value: AuditFieldValue,
	fieldKey?: string,
	personsSelectOptions?: Array<{ value: string; label: string }>,
	mipSelectOptions?: Array<{ value: string; label: string }>,
	securitiesSelectOptions?: Array<SecurityOption>,
	securityId?: string,
	tableName?: string,
	tSecurityTranslation?: (securityType?: SecurityType | LoanDefinitionType) => string
): string => {
	if (value === null || value === undefined) {
		return LONG_DASH;
	}

	if (
		fieldKey &&
		['destinationId', 'sourceId', 'sourcePersonId', 'targetPersonId'].includes(fieldKey) &&
		typeof value === 'string'
	) {
		return getEntityName(value, personsSelectOptions);
	}

	if (fieldKey && ['fromMipId', 'toMipId'].includes(fieldKey) && typeof value === 'string') {
		return getMipName(value, mipSelectOptions);
	}

	if (fieldKey === 'securityId' && typeof value === 'string') {
		return getSecurityName(value, securitiesSelectOptions);
	}

	if (tableName === 'PersonPerson' && fieldKey === 'relationType' && typeof value === 'string') {
		return formatPersonRelationType(value);
	}

	if (tableName === 'BankAccount' && fieldKey === 'status' && typeof value === 'string') {
		return normalizeScreamingSnakeToUI(value);
	}

	if (tableName === 'ShareAssociation' && fieldKey === 'number') {
		const security = securitiesSelectOptions?.find((option) => option.value === securityId);
		const unitDenominator = security?.unitDenominator || 1;
		const decimalPlaces = extractDecimalPrecisionFromUnitDenominator(unitDenominator);
		return (Number(value) / unitDenominator).toFixed(decimalPlaces);
	}

	if (
		fieldKey &&
		[
			'phoneNumber',
			'telephonePrefix',
			'iban',
			'accountNumber',
			'swiftCode',
			'number',
			'postCode',
			'houseNumber',
			'securityName',
			'loanDefinitionName',
		].includes(fieldKey)
	) {
		return value.toString();
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	if (tableName === 'Security') {
		if (fieldKey === 'type' && typeof value === 'string' && tSecurityTranslation) {
			return tSecurityTranslation(value as SecurityType);
		}

		if (fieldKey === 'issuerId' && typeof value === 'string') {
			return getEntityName(value, personsSelectOptions);
		}
	}

	if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
		const numericValue = typeof value === 'number' ? value : Number(value);

		if (fieldKey === 'units' && securityId) {
			const security = securitiesSelectOptions?.find((s) => s.value === securityId);
			const unitDenominator = security?.unitDenominator || 1;
			const decimalPlaces = extractDecimalPrecisionFromUnitDenominator(unitDenominator);

			const displayValue = numericValue / unitDenominator;
			return displayValue.toFixed(decimalPlaces);
		}

		return numericValue.toString();
	}

	// @db.Date columns serialize as plain YYYY-MM-DD (no 'T'), e.g. Transaction.date, dateOfBirth
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return formatBEDateToUiWithFallback(value, DATE_FORMATS.UI) as string;
	}

	if (typeof value === 'string' && value.includes('T')) {
		try {
			return formatStringDateToUI(value, DATE_FORMATS.UI) as string;
		} catch {
			return value;
		}
	}

	return value.toString();
};
